import React, { useState, useEffect } from 'react';
import { lawApi } from '../../lawApi';
import { useTheme } from '../../components/LawPortalLayout';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PRACTICE_AREAS = ['Corporate Law', 'Litigation', 'Criminal Law', 'Intellectual Property', 'Tax Law', 'Environmental Law', 'Immigration', 'Family Law', 'Health Law', 'International Law', 'Public Interest', 'Real Estate', 'Tech & Privacy', 'Constitutional Law'];
const REGIONS = ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest', 'Mid-Atlantic', 'Anywhere'];

function Section({ title, children, theme }) {
  const { dark, textSecondary, borderColor } = theme;
  const cardBg = dark ? 'bg-[#141618]' : 'bg-white';
  return (
    <div className={`${cardBg} border ${borderColor} rounded-lg`}>
      <div className={`px-5 py-3 border-b ${borderColor}`}>
        <h2 className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children, theme }) {
  const { textSecondary } = theme;
  return (
    <div>
      <label className={`block text-[11px] font-medium ${textSecondary} mb-1`}>{label}</label>
      {children}
    </div>
  );
}

export default function LawProfile() {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const theme = useTheme();
  const { dark, textPrimary, borderColor } = theme;

  const inputClass = `w-full h-9 px-3 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white text-[#1A1A1A]'} focus:outline-none focus:ring-1 focus:ring-[#C5A55A]/50`;
  const textareaClass = `w-full px-3 py-2 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white text-[#1A1A1A]'} focus:outline-none focus:ring-1 focus:ring-[#C5A55A]/50 min-h-[80px]`;
  const selectClass = inputClass;

  useEffect(() => {
    lawApi.getProfile().then(setProfile).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);

  const update = (field, value) => setProfile(p => ({ ...p, [field]: value }));
  const toggleArrayItem = (field, item) => {
    const arr = profile[field] || [];
    update(field, arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await lawApi.updateProfile(profile);
      setProfile(result);
      toast.success('Profile saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className={`h-96 animate-pulse rounded-lg ${dark ? 'bg-[#1C1E20]' : 'bg-[#E8E6E1]'}`} />;

  return (
    <div className="space-y-5" data-testid="law-profile">
      <div className="flex items-center justify-between">
        <h1 className={`text-lg font-semibold ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}>Profile & Intake</h1>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#1A1A1A] text-white text-xs rounded hover:bg-[#333] transition-colors disabled:opacity-50"
          data-testid="law-profile-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Profile
        </button>
      </div>

      <Section title="Biographical Information" theme={theme}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="First Name" theme={theme}>
            <input className={inputClass} value={profile.first_name || ''} onChange={e => update('first_name', e.target.value)} data-testid="law-first-name" />
          </Field>
          <Field label="Last Name" theme={theme}>
            <input className={inputClass} value={profile.last_name || ''} onChange={e => update('last_name', e.target.value)} />
          </Field>
          <Field label="Preferred Name" theme={theme}>
            <input className={inputClass} value={profile.preferred_name || ''} onChange={e => update('preferred_name', e.target.value)} />
          </Field>
          <Field label="Email" theme={theme}>
            <input className={inputClass} type="email" value={profile.email || ''} onChange={e => update('email', e.target.value)} />
          </Field>
          <Field label="Phone" theme={theme}>
            <input className={inputClass} value={profile.phone || ''} onChange={e => update('phone', e.target.value)} />
          </Field>
          <Field label="Date of Birth" theme={theme}>
            <input className={inputClass} type="date" value={profile.date_of_birth || ''} onChange={e => update('date_of_birth', e.target.value)} />
          </Field>
          <Field label="Citizenship" theme={theme}>
            <input className={inputClass} value={profile.citizenship || ''} onChange={e => update('citizenship', e.target.value)} />
          </Field>
          <Field label="Address" theme={theme}>
            <input className={inputClass} value={profile.address || ''} onChange={e => update('address', e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title="Academic History" theme={theme}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Undergraduate Institution" theme={theme}>
            <input className={inputClass} value={profile.undergraduate_school || ''} onChange={e => update('undergraduate_school', e.target.value)} data-testid="law-undergrad" />
          </Field>
          <Field label="Major" theme={theme}>
            <input className={inputClass} value={profile.undergraduate_major || ''} onChange={e => update('undergraduate_major', e.target.value)} />
          </Field>
          <Field label="Undergraduate GPA" theme={theme}>
            <input className={inputClass} type="number" step="0.01" min="0" max="4.0" value={profile.undergraduate_gpa || ''} onChange={e => update('undergraduate_gpa', parseFloat(e.target.value) || null)} data-testid="law-ugpa" />
          </Field>
          <Field label="Graduate School (if any)" theme={theme}>
            <input className={inputClass} value={profile.graduate_school || ''} onChange={e => update('graduate_school', e.target.value)} />
          </Field>
          <Field label="Graduate Degree" theme={theme}>
            <input className={inputClass} value={profile.graduate_degree || ''} onChange={e => update('graduate_degree', e.target.value)} />
          </Field>
          <Field label="Graduate GPA" theme={theme}>
            <input className={inputClass} type="number" step="0.01" min="0" max="4.0" value={profile.graduate_gpa || ''} onChange={e => update('graduate_gpa', parseFloat(e.target.value) || null)} />
          </Field>
        </div>
      </Section>

      <Section title="Test Scores" theme={theme}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="LSAT Score" theme={theme}>
            <input className={inputClass} type="number" min="120" max="180" value={profile.lsat_score || ''} onChange={e => update('lsat_score', parseInt(e.target.value) || null)} data-testid="law-lsat" />
          </Field>
          <Field label="LSAT Date" theme={theme}>
            <input className={inputClass} type="date" value={profile.lsat_date || ''} onChange={e => update('lsat_date', e.target.value)} />
          </Field>
          <Field label="LSAT Target" theme={theme}>
            <input className={inputClass} type="number" min="120" max="180" value={profile.lsat_target || ''} onChange={e => update('lsat_target', parseInt(e.target.value) || null)} />
          </Field>
          <Field label="GRE Verbal" theme={theme}>
            <input className={inputClass} type="number" value={profile.gre_verbal || ''} onChange={e => update('gre_verbal', parseInt(e.target.value) || null)} />
          </Field>
          <Field label="GRE Quant" theme={theme}>
            <input className={inputClass} type="number" value={profile.gre_quant || ''} onChange={e => update('gre_quant', parseInt(e.target.value) || null)} />
          </Field>
          <Field label="GRE Writing" theme={theme}>
            <input className={inputClass} type="number" step="0.5" value={profile.gre_writing || ''} onChange={e => update('gre_writing', parseFloat(e.target.value) || null)} />
          </Field>
        </div>
      </Section>

      <Section title="Work & Experience" theme={theme}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Current Employer" theme={theme}>
            <input className={inputClass} value={profile.current_employer || ''} onChange={e => update('current_employer', e.target.value)} />
          </Field>
          <Field label="Title" theme={theme}>
            <input className={inputClass} value={profile.current_title || ''} onChange={e => update('current_title', e.target.value)} />
          </Field>
          <Field label="Years of Work Experience" theme={theme}>
            <input className={inputClass} type="number" value={profile.years_work_experience || ''} onChange={e => update('years_work_experience', parseInt(e.target.value) || null)} />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Military Service" theme={theme}>
            <select className={selectClass} value={profile.military_service ? 'yes' : 'no'} onChange={e => update('military_service', e.target.value === 'yes')}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </Field>
          {profile.military_service && (
            <Field label="Branch" theme={theme}>
              <input className={inputClass} value={profile.military_branch || ''} onChange={e => update('military_branch', e.target.value)} />
            </Field>
          )}
        </div>
      </Section>

      <Section title="Character & Fitness" theme={theme}>
        <Field label="Any character & fitness disclosures?" theme={theme}>
          <select className={selectClass} value={profile.character_fitness_issues ? 'yes' : 'no'} onChange={e => update('character_fitness_issues', e.target.value === 'yes')}>
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </Field>
        {profile.character_fitness_issues && (
          <Field label="Details (confidential)" theme={theme}>
            <textarea className={textareaClass} value={profile.character_fitness_notes || ''} onChange={e => update('character_fitness_notes', e.target.value)} rows={3} />
          </Field>
        )}
      </Section>

      <Section title="Admissions Goals" theme={theme}>
        <Field label="Why Law School?" theme={theme}>
          <textarea className={textareaClass} value={profile.why_law_school || ''} onChange={e => update('why_law_school', e.target.value)} placeholder="Describe your motivation for pursuing a JD..." rows={3} />
        </Field>
        <Field label="Career Goals" theme={theme}>
          <textarea className={textareaClass} value={profile.career_goals || ''} onChange={e => update('career_goals', e.target.value)} placeholder="Where do you see your legal career heading?" rows={3} />
        </Field>
        <Field label="Practice Areas of Interest" theme={theme}>
          <div className="flex flex-wrap gap-1.5">
            {PRACTICE_AREAS.map(area => (
              <button key={area} onClick={() => toggleArrayItem('practice_areas_of_interest', area)}
                className={`px-2.5 py-1 text-[10px] rounded border transition-colors ${
                  (profile.practice_areas_of_interest || []).includes(area)
                    ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                    : `${dark ? 'border-[#333] text-[#888]' : 'border-[#DDD] text-[#666]'} hover:border-[#1A1A1A]`
                }`}>{area}</button>
            ))}
          </div>
        </Field>
        <Field label="Target Regions" theme={theme}>
          <div className="flex flex-wrap gap-1.5">
            {REGIONS.map(r => (
              <button key={r} onClick={() => toggleArrayItem('target_regions', r)}
                className={`px-2.5 py-1 text-[10px] rounded border transition-colors ${
                  (profile.target_regions || []).includes(r)
                    ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                    : `${dark ? 'border-[#333] text-[#888]' : 'border-[#DDD] text-[#666]'} hover:border-[#1A1A1A]`
                }`}>{r}</button>
            ))}
          </div>
        </Field>
      </Section>
    </div>
  );
}
